import React, { useEffect, useMemo, useState } from 'react';

interface User {
  empNumber: string;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

interface HandoverLetterModuleProps {
  show: boolean;
  onHide: () => void;

  /** Backward-compat (single handover) */
  letterId?: number;

  /** Preferred: bulk handover */
  letterIds?: number[];

  users: User[];
  departments: Department[];

  /** departmentId can be optional depending on backend; keep optional for flexibility */
  onHandover: (payload: { letterIds: number[]; empNo: string; departmentId?: number }) => void;
}

const HandoverLetterModule: React.FC<HandoverLetterModuleProps> = ({
  show,
  onHide,
  letterId,
  letterIds,
  users,
  departments,
  onHandover,
}) => {
  // ALWAYS compute from props so it reflects the latest selection.
  const effectiveIds = useMemo<number[]>(() => {
    if (Array.isArray(letterIds) && letterIds.length > 0) return Array.from(new Set(letterIds));
    return typeof letterId === 'number' ? [letterId] : [];
  }, [letterIds, letterId]);

  const [formData, setFormData] = useState<{ empNo: string; departmentId: string }>({
    empNo: '',
    departmentId: '',
  });

  useEffect(() => {
    if (show) setFormData({ empNo: '', departmentId: '' });
  }, [show, effectiveIds]);

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (effectiveIds.length === 0) {
      alert('No letters selected to handover.');
      return;
    }
    if (!formData.empNo) {
      alert('Please select an employee.');
      return;
    }
    if (!formData.departmentId) {
      alert('Please select a department.');
      return;
    }
    onHandover({
      letterIds: effectiveIds,
      empNo: formData.empNo,
      departmentId: parseInt(formData.departmentId, 10),
    });
  };

  return (
    <>
      <div
        className={`modal custom-modal fade ${show ? 'show' : ''}`}
        id="handoverLetterModal"
        role="dialog"
        style={{ display: show ? 'block' : 'none' }}
        aria-modal={show ? 'true' : undefined}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Handover Letter{effectiveIds.length > 1 ? 's' : ''}{' '}
                {effectiveIds.length > 0 && (
                  <small className="text-muted">({effectiveIds.length} selected)</small>
                )}
              </h5>
              <button className="btn-close" onClick={onHide} aria-label="Close">
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="modal-body">
              {/* Show all selected IDs clearly */}
              <div className="mb-3">
                <label className="col-form-label">Selected Letter ID{effectiveIds.length > 1 ? 's' : ''}</label>
                <div className="form-control" style={{ maxHeight: 120, overflowY: 'auto' }}>
                  {effectiveIds.length ? effectiveIds.join(', ') : '—'}
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="col-form-label">
                    Employee <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="empNo"
                    value={formData.empNo}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Employee</option>
                    {users.map((u) => (
                      <option key={u.empNumber} value={u.empNumber}>
                        {u.name} ({u.empNumber})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="col-form-label">
                    Department <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="departmentId"
                    value={formData.departmentId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="modal-btn d-flex gap-2 justify-content-end">
                  <button type="button" className="btn btn-primary" onClick={onHide}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={effectiveIds.length === 0 || !formData.empNo || !formData.departmentId}
                  >
                    Handover
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {show && <div className="modal-backdrop fade show" />}
    </>
  );
};

export default HandoverLetterModule;
